import React from 'react';
import styled from 'styled-components';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { useWelcomeInPartnerConversions } from '../hooks/useWelcomeInPartnerConversions';
import { hasAnyPermission, Permission } from '../utils/roles';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderTitle = styled.h1`
  margin: 0 0 1.25rem;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const SummaryCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1rem 1.1rem;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
`;

const SummaryLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--onSurfaceVariant);
  margin-bottom: 0.35rem;
`;

const SummaryValue = styled.div`
  font-size: 1.65rem;
  font-weight: 700;
  color: var(--onSurface);
`;

const SummaryHint = styled.div`
  margin-top: 0.35rem;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }

  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const ClickableRow = styled.tr<{ $clickable?: boolean }>`
  ${({ $clickable }) =>
    $clickable
      ? `
    cursor: pointer;
    &:hover {
      background: var(--surfaceVariant);
    }
  `
      : ''}
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0 0 1rem;
  font-size: 0.9rem;
`;

export const WelcomeInPartnerConversionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useCurrentUserProfile(user?.id);
  const { rows, summary, isLoading, error } = useWelcomeInPartnerConversions();

  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canViewUsers = hasAnyPermission(roles, [Permission.VIEW_USERS]);
  const canViewPartners = hasAnyPermission(roles, [
    Permission.MANAGE_VOLUNTEERS,
    Permission.VIEW_PARTNER_REQUESTS,
  ]);

  if (authLoading || profileLoading) {
    return <Page>Loading...</Page>;
  }

  if (!canViewUsers || !canViewPartners) {
    return <Navigate to="/profile" replace />;
  }

  return (
    <Page>
      <HeaderTitle>Welcome In → New Partners</HeaderTitle>
      <HelperText>
        People who opted into Welcome In and also submitted a new partner request, matched by email
        or phone. A partner submission counts once even if it matches more than one opt-in.
      </HelperText>

      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>Welcome In opt-ins</SummaryLabel>
          <SummaryValue>{summary.welcomeInTotal}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>New partner submissions</SummaryLabel>
          <SummaryValue>{summary.partnerTotal}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>In both</SummaryLabel>
          <SummaryValue>{summary.convertedTotal}</SummaryValue>
          <SummaryHint>Matched opt-in records</SummaryHint>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Partners also in Welcome In</SummaryLabel>
          <SummaryValue>{summary.partnerOverlapPercent}%</SummaryValue>
          <SummaryHint>
            {summary.matchedPartnerTotal} of {summary.partnerTotal} submissions
          </SummaryHint>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Opt-ins who became partners</SummaryLabel>
          <SummaryValue>{summary.optInConversionPercent}%</SummaryValue>
          <SummaryHint>
            {summary.convertedTotal} of {summary.welcomeInTotal} opt-ins
          </SummaryHint>
        </SummaryCard>
      </SummaryGrid>

      {isLoading && <p>Loading conversions...</p>}
      {error && <p>Error loading conversions</p>}
      {!isLoading && !error && (
        <TableCard>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Phone</th>
                  <th>Welcome In</th>
                  <th>Became Partner</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5}>No matching records found.</td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <ClickableRow
                      key={`${row.welcomeInId}-${row.partnerRequestId}`}
                      $clickable={Boolean(row.userId)}
                      onClick={() => {
                        if (row.userId) {
                          navigate(`/admin/members/${row.userId}`);
                        }
                      }}
                    >
                      <td>{row.firstName}</td>
                      <td>{row.lastName}</td>
                      <td>{row.phone ?? '-'}</td>
                      <td>{formatDateTime(row.welcomeInDate)}</td>
                      <td>{formatDateTime(row.partnerDate)}</td>
                    </ClickableRow>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrap>
        </TableCard>
      )}
    </Page>
  );
};

export default WelcomeInPartnerConversionsPage;

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}
